"use client"

import React, { useMemo } from "react"
import { removePromoCodeFromCurrentBasketAction } from "@/actions/basketActions"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { ShoppingCartIcon, TagIcon, XCircleIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import { formatPrice } from "@/lib/helper"
import type { BasketLineItemData, BasketPromotionCode, BasketTotalData } from "@/lib/storefront/basket/basket"
import { Button } from "@/components/ui/components"
import { cn } from "@/components/ui/helpers/utils"
import BasicCard from "@/components/common/BasicCard"

type BasketDiscountTier = {
  threshold: number
  discountPercent: number
}

const DISCOUNT_TIERS: BasketDiscountTier[] = [
  { threshold: 150, discountPercent: 5 },
  { threshold: 200, discountPercent: 10 },
  { threshold: 300, discountPercent: 15 },
]

interface BasketCartDiscountProgressProps {
  basketLineItems: BasketLineItemData[]
  basketPromotionCodes?: BasketPromotionCode[]
  basketTotalData: BasketTotalData
  isoLocale: string
}

function clampProgress(value: number) {
  return Math.max(0, Math.min(value, 100))
}

function formatThresholdPrice(isoLocale: string, value: number, currency: string) {
  return formatPrice(isoLocale, value, currency).replace(/([,.]00)(\s|$)/, "$2")
}

export default function BasketCartDiscountProgress({
  basketLineItems,
  basketPromotionCodes,
  basketTotalData,
  isoLocale,
}: BasketCartDiscountProgressProps) {
  const t = useTranslations("storefront")
  const { setBasketData, setBasketPromotionCodes } = useUbStorefrontState()
  const activePromotionCode = basketPromotionCodes?.[0]?.code
  const hasActivePromotionCode = Boolean(activePromotionCode)
  const currentBasketValue = useMemo(() => {
    return basketLineItems.reduce((total, lineItem) => {
      if (lineItem.product?.isDoNotPromoteAll) {
        return total
      }

      const lineItemNormalNet =
        lineItem.totalListPriceNet ?? lineItem.originalLineItem.pricing?.undiscountedPrice?.net?.value ?? 0
      const lineItemNonCartDiscountNet = lineItem.discountType !== "VOUCHER" ? lineItem.discountAmount : 0

      return total + lineItemNormalNet - lineItemNonCartDiscountNet
    }, 0)
  }, [basketLineItems])
  const hasNonDiscountableLineItems = useMemo(() => {
    return basketLineItems.some((lineItem) => lineItem.product?.isDoNotPromoteAll)
  }, [basketLineItems])
  const currency = basketTotalData.currency
  const maxThreshold = DISCOUNT_TIERS[DISCOUNT_TIERS.length - 1]?.threshold ?? 0

  const nextTier = useMemo(() => {
    return DISCOUNT_TIERS.find((tier) => currentBasketValue < tier.threshold)
  }, [currentBasketValue])

  const progressPercent = maxThreshold > 0 ? clampProgress((currentBasketValue / maxThreshold) * 100) : 0

  function removePromotionCode() {
    if (!activePromotionCode) {
      return
    }

    removePromoCodeFromCurrentBasketAction(activePromotionCode).then((result) => {
      setBasketData(result.basketData)
      setBasketPromotionCodes([])
    })
  }

  return (
    <BasicCard className="min-h-0 p-0 shadow-sm sm:p-0">
      <div className="flex flex-col gap-4 p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <TagIcon className="size-7 fill-primary/20" strokeWidth={2.25} aria-hidden="true" />
            </span>
            <div>
              <p className="text-base font-bold">{t("basket.cart_discount.title")}</p>
              {hasActivePromotionCode ? (
                <div className="flex flex-col gap-2 text-sm sm:flex-row sm:items-center">
                  <p>
                    {t("basket.cart_discount.inactive_with_code_before")}{" "}
                    <span className="font-bold">{activePromotionCode}</span>{" "}
                    {t("basket.cart_discount.inactive_with_code_after")}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="smNoGrow"
                    className="h-7 gap-1 self-start rounded-sm px-2 py-0 text-xs"
                    onClick={removePromotionCode}
                  >
                    <XCircleIcon className="size-4" aria-hidden="true" />
                    {t("basket.cart_discount.remove_code")}
                  </Button>
                </div>
              ) : (
                <p className="text-sm">
                  {nextTier ? (
                    <>
                      {t("basket.cart_discount.remaining_prefix")}{" "}
                      <span className="font-bold">
                        {formatPrice(isoLocale, nextTier.threshold - currentBasketValue, currency)}
                      </span>{" "}
                      {t("basket.cart_discount.remaining_suffix")}
                    </>
                  ) : (
                    <span className="font-bold text-primary">{t("basket.cart_discount.max_tier_reached")}</span>
                  )}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className={cn("px-1 sm:px-3", { "opacity-35 grayscale": hasActivePromotionCode })}>
          <div className="relative h-20">
            <div className="absolute top-9 right-0 left-0 h-2 rounded-full bg-gray-200" aria-hidden="true" />
            <div
              className="absolute top-9 left-0 h-2 rounded-full bg-primary transition-[width] duration-500"
              style={{ width: `${progressPercent}%` }}
              aria-hidden="true"
            />
            <div className="absolute top-0 left-0 -translate-x-1">
              <span className="text-sm font-bold">{formatThresholdPrice(isoLocale, 0, currency)}</span>
              <span
                className="absolute top-10 left-1 size-4 -translate-y-1/2 rounded-full border-2 border-primary bg-primary"
                aria-hidden="true"
              />
            </div>

            {DISCOUNT_TIERS.map((tier) => {
              const tierPosition = maxThreshold > 0 ? (tier.threshold / maxThreshold) * 100 : 0
              const isReached = currentBasketValue >= tier.threshold
              const isNext = nextTier?.threshold === tier.threshold

              return (
                <div
                  key={tier.threshold}
                  className="absolute top-0 h-full -translate-x-1/2 whitespace-nowrap"
                  style={{ left: `${tierPosition}%` }}
                >
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 text-sm font-bold">
                    {formatThresholdPrice(isoLocale, tier.threshold, currency)}
                  </span>
                  <span
                    className={
                      isNext
                        ? "absolute top-10 left-1/2 size-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-primary bg-white"
                        : isReached
                          ? "absolute top-10 left-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-primary bg-primary"
                          : "absolute top-10 left-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-gray-400 bg-gray-400"
                    }
                    aria-hidden="true"
                  />
                  <span
                    className={
                      isNext
                        ? "absolute top-14 left-1/2 -translate-x-1/2 rounded-sm bg-primary/10 px-2 py-1 text-sm font-bold text-primary"
                        : "absolute top-14 left-1/2 -translate-x-1/2 rounded-sm bg-gray-100 px-2 py-1 text-sm font-bold text-gray-600"
                    }
                  >
                    {tier.discountPercent} %
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <div
        className={cn(
          "flex flex-col gap-3 border-t border-gray-200 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between sm:px-5",
          {
            "text-gray-500": hasActivePromotionCode,
          }
        )}
      >
        <span className="inline-flex items-center gap-2 font-bold">
          <ShoppingCartIcon
            className={cn("size-5 text-green-500", { "text-gray-400": hasActivePromotionCode })}
            strokeWidth={2}
            aria-hidden="true"
          />
          {t("basket.cart_discount.current_basket")}
          {hasNonDiscountableLineItems ? ` ${t("basket.cart_discount.eligible_items_only")}` : ""}:{" "}
          <span className={cn("text-primary", { "text-gray-500": hasActivePromotionCode })}>
            {formatPrice(isoLocale, currentBasketValue, currency)}
          </span>
        </span>
        {!hasActivePromotionCode && nextTier && (
          <span>
            {t("basket.cart_discount.next_tier")}:{" "}
            <span className="font-bold text-primary">
              {t("basket.cart_discount.discount", { percent: nextTier.discountPercent })}
            </span>{" "}
            {t("basket.cart_discount.from")} {formatThresholdPrice(isoLocale, nextTier.threshold, currency)}
          </span>
        )}
      </div>
    </BasicCard>
  )
}
